import { getRefreshedTokens } from "@ch/api";
import { getDateOnly } from "@ch/helpers/utils";
import useLogout from "@ch/helpers/hooks/useLogout";
import useDelayedCallback from "@ch/helpers/hooks/useDelayedCallback";
import { useDisclosure } from "@ch/helpers/hooks/useDisclosure";
import useFirebaseAnalytics from "@ch/helpers/utils/firebase/hooks/useFirebaseAnalytics";
import { FirebaseEventEnum } from "@ch/helpers/utils/firebase/types";
import { handleError } from "@ch/helpers/utils/handleError";
import { NotifyError, NotifyInfo } from "@ch/helpers/utils/helpers";
import { updateAppKemState } from "@ch/redux-store/kem/actions";
import type { AppState } from "@ch/redux-store/root-reducer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import useKemUserSession from "@ch/helpers/hooks/useKemUserSession";
import { audioManager } from "../utils/audioManager";
import { toHttpUrl } from "../utils/audioUrl";
import formatKemThinkingStep from "../utils/formatKemThinkingStep";
import getKemChatPayloadToSend from "../utils/getKemChatPayloadToSend";
import KemQaSocket, {
  type KemQaCompletionData,
  type KemQaSocketState,
} from "../utils/kemQaSocket";
import type {
  TGetChatObject,
  TKemMessagePayload,
  TSendMessageProps,
} from "../utils/types";
import uploadAndTranscribe from "../utils/uploadAndTranscribe";
import useGetHistory from "./useGetHistory";
import useKemDefaultPayload from "./useKemDefaultPayload";
import useKemHealthCheck from "./useKemHealthCheck";
import useKemMarkMessagesSeen from "./useKemMarkMessagesSeen";

const historyItemsPerBatch = 10;
const delayToShowUpgradeNoticeInMs = 1500;

const useKemChat = () => {
  const dispatch = useDispatch();
  const userId = useSelector((s: AppState) => s.user.currentUser?.id);
  const currentUser = useSelector((s: AppState) => s.user.currentUser);
  const kemUserSession = useSelector((s: AppState) => s.kem.kemUserSession);
  const kemUserSessionRef = useRef(kemUserSession);
  kemUserSessionRef.current = kemUserSession;
  const userLocation = useSelector((s: AppState) => s.user.location);

  const userFullName =
    currentUser?.name?.trim?.() ??
    ([currentUser?.firstName, currentUser?.lastName]
      .filter(Boolean)
      .join(" ") ||
      "You");

  const kemCoachingState = kemUserSession?.coach_session_state;

  const hasRemainingTokens =
    kemUserSession?.remaining_tokens && kemUserSession.remaining_tokens > 0;

  const canSpeakToKem =
    !!hasRemainingTokens ||
    kemUserSession?.subscription_status === "subscribed";

  const isLimitReached = useMemo(
    () => kemUserSession != null && !canSpeakToKem,
    [canSpeakToKem, kemUserSession],
  );

  const { initiateKemUserSession } = useKemUserSession();
  const { logoutUser } = useLogout();

  const [socketState, setSocketState] =
    useState<KemQaSocketState>("DISCONNECTED");
  const [currentChat, setCurrentChat] = useState<TKemMessagePayload[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [loadingBotChat, setLoadingBotChat] = useState<
    "sending" | "streaming" | undefined
  >(undefined);
  const [thinkingStep, setThinkingStep] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [hasSentPrompt, setHasSentPrompt] = useState(false);

  const {
    isOpen: isUpgradeNoticeModalOpen,
    onClose: onCloseUpgradeNoticeModal,
    onOpen: onOpenUpgradeNoticeModal,
  } = useDisclosure();

  const blockSpeakToKem = useCallback(() => {
    NotifyError("Please subscribe to a program to talk to Kem.");
    onOpenUpgradeNoticeModal();
  }, [onOpenUpgradeNoticeModal]);

  const ensureCanSpeakToKem = useCallback(() => {
    if (!canSpeakToKem) {
      blockSpeakToKem();
      return false;
    }

    return true;
  }, [blockSpeakToKem, canSpeakToKem]);

  const socketRef = useRef<KemQaSocket | null>(null);
  const currentBotMessageIdRef = useRef<string | null>(null);
  const hasSentMemberPayloadForConnectionRef = useRef(false);
  const hasLoadedHistoryRef = useRef(false);
  const isMountedRef = useRef(true);
  const mostRecentKemChatIdRef = useRef<string | null>(null);
  const chatContainerRef = useRef<HTMLElement | null>(null);
  const totalStreamedCharsRef = useRef<number>(0);

  const { LogEvent } = useFirebaseAnalytics();
  const { defaultChatPayload } = useKemDefaultPayload();
  const { kemHealthIsHealthy } = useKemHealthCheck();
  const { handleMarkMessageVisible } = useKemMarkMessagesSeen({
    setCurrentChat,
  });

  const chatLocation = useMemo(() => {
    const { latitude, longitude } = userLocation ?? {};

    if (
      typeof latitude === "number" &&
      Number.isFinite(latitude) &&
      typeof longitude === "number" &&
      Number.isFinite(longitude)
    ) {
      return `${latitude},${longitude}`;
    }

    return defaultChatPayload?.location?.trim();
  }, [defaultChatPayload?.location, userLocation]);

  const defaultChatProps = useMemo(() => {
    const latestBMI = defaultChatPayload?.latest_bmi?.split(" ")[0];

    return {
      ...defaultChatPayload,
      bmi_measurement_date: getDateOnly(
        defaultChatPayload?.bmi_measurement_date,
      ),
      bp_measurement_date: getDateOnly(defaultChatPayload?.bp_measurement_date),
      date_of_birth: getDateOnly(defaultChatPayload?.date_of_birth),
      latest_bmi: latestBMI ? Number(latestBMI) : undefined,
      exercise_duration_last_30_days:
        defaultChatPayload?.exercise_duration_last_30_days?.toString(),
      location: chatLocation,
      user_full_name: defaultChatPayload?.user_full_name ?? userFullName,
      user_id: defaultChatPayload?.user_id ?? userId,
      blood_sugar_measurement_date: getDateOnly(
        defaultChatPayload?.blood_sugar_measurement_date,
      ),
      cholesterol_measurement_date: getDateOnly(
        defaultChatPayload?.cholesterol_measurement_date,
      ),
      height_measurement_date: getDateOnly(
        defaultChatPayload?.height_measurement_date,
      ),
      weight_measurement_date: getDateOnly(
        defaultChatPayload?.weight_measurement_date,
      ),
      weight_goal_measurement_date: getDateOnly(
        defaultChatPayload?.weight_goal_measurement_date,
      ),
      waist_circumference_measurement_date: getDateOnly(
        defaultChatPayload?.waist_circumference_measurement_date,
      ),
      hbaic_measurement_date: getDateOnly(
        defaultChatPayload?.hbaic_measurement_date,
      ),
      eye_examination_measurement_date: getDateOnly(
        defaultChatPayload?.eye_examination_measurement_date,
      ),
      foot_examination_measurement_date: getDateOnly(
        defaultChatPayload?.foot_examination_measurement_date,
      ),
      health_condition_measurement_date: getDateOnly(
        defaultChatPayload?.health_condition_measurement_date,
      ),
      procedure_surgeries_measurement_date: getDateOnly(
        defaultChatPayload?.procedure_surgeries_measurement_date,
      ),
      prescription_measurement_date: getDateOnly(
        defaultChatPayload?.prescription_measurement_date,
      ),
    };
  }, [chatLocation, defaultChatPayload, userFullName, userId]);

  const isReady =
    socketState === "READY" ||
    socketState === "COMPLETE" ||
    socketState === "STREAMING" ||
    socketState === "RECONNECTING" ||
    socketState === "ERROR";

  const kemSocketReachable = useMemo(
    () => socketState !== "ERROR" && socketState !== "DISCONNECTED",
    [socketState],
  );

  const isConnecting = useMemo(
    () => socketState === "CONNECTING",
    [socketState],
  );

  useEffect(() => {
    if (currentChat.length > 0) {
      mostRecentKemChatIdRef.current =
        currentChat[currentChat.length - 1]?.id ?? null;
    }
  }, [currentChat]);

  const {
    getNextChatHistory,
    getIsLastPage,
    getChatHistory,
    hasFetchedInitialData,
    currentPage,
  } = useGetHistory({ pageLimit: historyItemsPerBatch });

  const onScrollToBottom = useCallback(() => {
    const el = chatContainerRef.current;
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }
  }, []);

  const followLatestMessageScrollCustomRef = useRef<
    ((options?: { immediate?: boolean }) => void) | null
  >(null);

  const setLatestMessageFollowScroll = useCallback(
    (fn: ((options?: { immediate?: boolean }) => void) | null) => {
      followLatestMessageScrollCustomRef.current = fn;
    },
    [],
  );

  const followLatestMessageAfterSend = useCallback(
    (followScrollOptions?: { immediate?: boolean }) => {
      const custom = followLatestMessageScrollCustomRef.current;
      if (custom) {
        custom(followScrollOptions);
        return;
      }
      onScrollToBottom();
    },
    [onScrollToBottom],
  );

  const handleGetChatHistory = useCallback(
    async (options?: { force?: boolean }) => {
      const force = options?.force ?? false;

      if (!force && hasLoadedHistoryRef.current && currentChat.length > 0) {
        return;
      }

      const showInitialLoader = currentChat.length === 0;
      if (showInitialLoader) {
        setIsLoadingHistory(true);
      }

      try {
        const items = await getChatHistory();
        setCurrentChat(items);
      } catch (error) {
        handleError(error, undefined, true);
      } finally {
        hasLoadedHistoryRef.current = true;
        if (showInitialLoader) {
          setIsLoadingHistory(false);
        }
      }
    },
    [currentChat.length, getChatHistory],
  );

  const kemStatusMessage =
    thinkingStep ?? (loadingBotChat === "sending" ? "Kem is thinking..." : "");

  const kemIsTyping = kemStatusMessage;

  const clearThinkingStep = useCallback(() => {
    setThinkingStep(null);
  }, []);

  const isInitialHistoryLoading = useMemo(
    () => isLoadingHistory && currentChat.length === 0,
    [currentChat.length, isLoadingHistory],
  );

  const handleResetChatHistory = useCallback(async () => {
    hasFetchedInitialData.current = false;
    currentPage.current = 1;
    await handleGetChatHistory({ force: true });
  }, [currentPage, handleGetChatHistory, hasFetchedInitialData]);

  const handleGetNextRecentHistory = useCallback(async (): Promise<number> => {
    if (getIsLastPage()) return 0;

    setIsLoadingMoreHistory(true);
    try {
      const next = await getNextChatHistory();

      if (next.length === 0) return 0;

      setCurrentChat((prev) => {
        const combined = [...next, ...prev];
        const seen = new Set<string>();
        return combined.filter((item) => {
          const id = item.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      });

      return next.length;
    } catch (error) {
      handleError(error, undefined, true);
      return 0;
    } finally {
      setIsLoadingMoreHistory(false);
    }
  }, [getIsLastPage, getNextChatHistory]);

  const handleLoadingBotChat = useCallback(
    (state: "sending" | "streaming" | undefined) => {
      setLoadingBotChat(state);
    },
    [],
  );

  const handleCancelCurrentBotMessage = useCallback(() => {
    NotifyInfo("Please wait for the current response to complete");
  }, []);

  const setChatContainerRef = useCallback((el: HTMLElement | null) => {
    chatContainerRef.current = el;
  }, []);

  const setMessageAudioUrl = useCallback((messageId: string, url: string) => {
    setCurrentChat((prev) =>
      prev.map((m) =>
        m.id === messageId ? { ...m, output_audio_url: url } : m,
      ),
    );
  }, []);

  const handleSetRemainingTokens = useCallback(
    (remainingTokens?: number) => {
      const session = kemUserSessionRef.current;
      if (!session) {
        return;
      }

      dispatch(
        updateAppKemState({
          kemUserSession: {
            ...session,
            remaining_tokens: remainingTokens,
          },
        }),
      );
    },
    [dispatch],
  );

  const getChatObject = useCallback(
    ({ createdAt, id, message, modifiedAt, senderEnum }: TGetChatObject) => ({
      ...defaultChatProps,
      id,
      sender_enum: senderEnum,
      message,
      created_at: createdAt,
      modified_at: modifiedAt,
    }),
    [defaultChatProps],
  );

  const handleSetUserChat = useCallback(
    (message: string) => {
      setCurrentChat((prev) => [
        ...prev,
        getChatObject({
          message,
          id: uuidv4(),
          senderEnum: "user",
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
        }),
      ]);
    },
    [getChatObject],
  );

  const handleSetBotChat = useCallback(
    (messageChunk: string, chatObject: TKemMessagePayload) => {
      setCurrentChat((prev) => {
        const newChats = [...prev];
        const chatIndex = newChats.findIndex((c) => c.id === chatObject.id);

        if (chatIndex !== -1) {
          newChats[chatIndex] = {
            ...newChats[chatIndex],
            message: `${newChats[chatIndex].message}${messageChunk}`,
          };
        } else {
          newChats.push({ ...chatObject, message: messageChunk });
        }

        return newChats;
      });
    },
    [],
  );

  const handleSetBotMessageId = useCallback((oldId: string, newId: string) => {
    setCurrentChat((prev) => {
      const newChats = [...prev];
      const chatIndex = newChats.findIndex((c) => c.id === oldId);

      if (chatIndex !== -1) {
        newChats[chatIndex] = { ...newChats[chatIndex], id: newId };
      }

      return newChats;
    });
  }, []);

  const onPlayAudio = useCallback(async (url: string) => {
    void audioManager.playUrl(toHttpUrl(url));
  }, []);

  const handleSetChatAudioUrl = useCallback((chatId: string, url: string) => {
    mostRecentKemChatIdRef.current = chatId;
    setCurrentChat((prev) => {
      const chatIndex = prev.findIndex((chat) => chat.id === chatId);

      if (chatIndex !== -1) {
        return [
          ...prev.slice(0, chatIndex),
          { ...prev[chatIndex], output_audio_url: url },
          ...prev.slice(chatIndex + 1),
        ];
      }

      return prev;
    });
  }, []);

  const handleSetBotResources = useCallback(
    (chatId: string, resources: TKemMessagePayload["resources"]) => {
      if (!resources?.length) {
        return;
      }

      setCurrentChat((prev) => {
        const chatIndex = prev.findIndex((chat) => chat.id === chatId);

        if (chatIndex === -1) {
          return prev;
        }

        return [
          ...prev.slice(0, chatIndex),
          { ...prev[chatIndex], resources },
          ...prev.slice(chatIndex + 1),
        ];
      });
    },
    [],
  );

  const buildMemberPayload = useCallback(
    (overrides?: Partial<TKemMessagePayload>) => {
      return getKemChatPayloadToSend({ ...defaultChatProps, ...overrides });
    },
    [defaultChatProps],
  );

  const initializeSocket = useCallback(() => {
    if (socketRef.current) {
      return;
    }

    const socket = new KemQaSocket();
    socketRef.current = socket;

    void socket.connect({
      onStateChange: (state) => {
        if (
          state === "CONNECTING" ||
          state === "DISCONNECTED" ||
          state === "RECONNECTING"
        ) {
          hasSentMemberPayloadForConnectionRef.current = false;
        }

        setSocketState(state);

        if (state === "STREAMING") {
          clearThinkingStep();
          handleLoadingBotChat("streaming");
        } else if (state === "COMPLETE" || state === "READY") {
          clearThinkingStep();
          handleLoadingBotChat(undefined);
        }
      },

      onThinking: (payload) => {
        setThinkingStep(formatKemThinkingStep(payload));
      },

      onChunk: (chunk) => {
        clearThinkingStep();
        currentBotMessageIdRef.current ??= uuidv4();

        totalStreamedCharsRef.current += chunk.length;

        handleSetBotChat(
          chunk,
          getChatObject({
            createdAt: new Date().toISOString(),
            id: currentBotMessageIdRef.current,
            modifiedAt: new Date().toISOString(),
            senderEnum: "kem",
            message: "",
          }),
        );
      },

      onComplete: (data: KemQaCompletionData) => {
        clearThinkingStep();
        const tempId = currentBotMessageIdRef.current;

        if (
          data.output_audio_url &&
          data.output_audio_url.trim() !== "" &&
          tempId
        ) {
          handleSetChatAudioUrl(tempId, data.output_audio_url);
          void onPlayAudio(data.output_audio_url);
        }

        if (data.infographic && data.infographic.trim() !== "") {
          const infographicMessageId = tempId ?? uuidv4();

          handleSetBotChat(
            `\n\n${data.infographic}`,
            getChatObject({
              createdAt: new Date().toISOString(),
              id: infographicMessageId,
              modifiedAt: new Date().toISOString(),
              senderEnum: "kem",
              message: "",
            }),
          );
        }

        if (tempId && data.resources && data.resources.length > 0) {
          handleSetBotResources(tempId, data.resources);
        }

        if (
          tempId &&
          data.kem_message_id &&
          data.kem_message_id.trim() !== ""
        ) {
          handleSetBotMessageId(tempId, data.kem_message_id);
        }

        if (data.remaining_tokens !== undefined) {
          handleSetRemainingTokens(data.remaining_tokens);
        }

        LogEvent(FirebaseEventEnum.KEM_CHAT_MESSAGE, {
          timestamp: new Date().toISOString(),
          type: "incoming",
          messageCharCount: totalStreamedCharsRef.current,
        });
        totalStreamedCharsRef.current = 0;

        currentBotMessageIdRef.current = null;
        followLatestMessageAfterSend({ immediate: true });
      },

      onError: (error) => {
        clearThinkingStep();

        if (!isMountedRef.current) {
          return;
        }

        if (error === "unauthorized") {
          void (async () => {
            const tokens = await getRefreshedTokens();
            if (tokens && socketRef.current) {
              socketRef.current.reconnect();
              return;
            }
            NotifyError("Session expired. Please login again.");
            void logoutUser();
          })();
          return;
        }

        NotifyError(error);
      },

      onMemberAck: () => {
        // Q/A has no greeting after member-payload ack
      },
    });
  }, [
    LogEvent,
    clearThinkingStep,
    followLatestMessageAfterSend,
    getChatObject,
    handleLoadingBotChat,
    handleSetBotChat,
    handleSetBotMessageId,
    handleSetBotResources,
    handleSetChatAudioUrl,
    handleSetRemainingTokens,
    logoutUser,
    onPlayAudio,
  ]);

  const handleSendMessage = useCallback(
    async (props: TSendMessageProps) => {
      const text = props.message?.trim();
      if (!text) {
        NotifyInfo("Message cannot be empty");
        return;
      }

      const isOnline =
        typeof navigator !== "undefined" ? navigator.onLine : true;
      if (!isOnline) {
        NotifyError("No internet connection. Please connect to the internet.");
        return;
      }

      try {
        const effectiveType = (
          navigator as unknown as { connection?: { effectiveType?: string } }
        )?.connection?.effectiveType;
        if (
          effectiveType &&
          (effectiveType === "2g" || effectiveType === "slow-2g")
        ) {
          NotifyInfo(
            "Your internet connection may be unstable; you may experience delays in sending messages",
          );
        }
      } catch {
        // ignore capability issues
      }

      if (!userId) {
        NotifyError("User not found");
        return;
      }

      if (socketState === "ERROR" && socketRef.current) {
        NotifyInfo("Reconnecting...");
        socketRef.current.reconnect();
        return;
      }

      if (socketState === "RECONNECTING") {
        NotifyInfo("Reconnecting, please wait...");
        return;
      }

      if (!socketRef.current?.isConnected()) {
        NotifyError("Socket is not connected");
        return;
      }

      if (socketState === "STREAMING" || loadingBotChat) {
        NotifyInfo("Please wait for the current response to complete");
        return;
      }

      if (socketState !== "READY" && socketState !== "COMPLETE") {
        NotifyInfo("Connecting, please wait...");
        return;
      }

      if (!ensureCanSpeakToKem()) {
        return;
      }

      handleSetUserChat(text);
      followLatestMessageAfterSend();
      setHasSentPrompt(true);

      currentBotMessageIdRef.current = null;

      LogEvent(FirebaseEventEnum.KEM_CHAT_MESSAGE, {
        timestamp: new Date().toISOString(),
        type: "outgoing",
        messageCharCount: text.length,
      });

      clearThinkingStep();
      setLoadingBotChat("sending");

      const trimmedAudioUrl = props.audioUrl?.trim();

      const sent = trimmedAudioUrl
        ? socketRef.current.sendChatAudio({
            text,
            audio_url: trimmedAudioUrl,
          })
        : socketRef.current.sendChatMessage(text);

      if (!sent) {
        clearThinkingStep();
        setLoadingBotChat(undefined);
      }
    },
    [
      LogEvent,
      clearThinkingStep,
      ensureCanSpeakToKem,
      followLatestMessageAfterSend,
      handleSetUserChat,
      loadingBotChat,
      socketState,
      userId,
    ],
  );

  const handleSendVoice = useCallback(
    async (blob: Blob) => {
      try {
        setIsTranscribing(true);
        const { data } = await uploadAndTranscribe(
          blob,
          "voice-message.webm",
          userId,
        );
        const transcript = (data?.text || "").trim();
        const audioUrl = (
          (data as { audio_file_url?: string })?.audio_file_url ?? data?.url
        )?.trim();

        if (!transcript) {
          NotifyError("Failed to transcribe audio. Please try again.");
          return;
        }

        await handleSendMessage({
          message: transcript,
          audioUrl,
          outputAudio: true,
        });
      } catch (err) {
        handleError(err, undefined, true);
      } finally {
        setIsTranscribing(false);
      }
    },
    [handleSendMessage, userId],
  );

  const handleRetryConnection = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.reconnect();
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (hasLoadedHistoryRef.current || currentChat.length > 0) return;

    void handleGetChatHistory();
  }, [userId, currentChat.length, handleGetChatHistory]);

  useEffect(() => {
    if (
      defaultChatPayload &&
      socketState === "DISCONNECTED" &&
      !socketRef.current
    ) {
      initializeSocket();
    }
  }, [defaultChatPayload, socketState, initializeSocket]);

  useEffect(() => {
    if (
      hasLoadedHistoryRef.current &&
      !isLoadingHistory &&
      socketState === "CONNECTED" &&
      socketRef.current?.isConnected() &&
      defaultChatPayload &&
      !hasSentMemberPayloadForConnectionRef.current
    ) {
      const memberPayload = buildMemberPayload();
      const sent = socketRef.current.sendMemberPayload(memberPayload);

      if (sent) {
        hasSentMemberPayloadForConnectionRef.current = true;
      }
    }
  }, [socketState, defaultChatPayload, buildMemberPayload, isLoadingHistory]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        socketRef.current &&
        !socketRef.current.isConnected() &&
        (socketState === "ERROR" || socketState === "DISCONNECTED")
      ) {
        setTimeout(() => {
          if (socketRef.current && !socketRef.current.isConnected()) {
            socketRef.current.reconnect();
          }
        }, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [socketState]);

  useDelayedCallback({
    callback: onOpenUpgradeNoticeModal,
    delay: delayToShowUpgradeNoticeInMs,
    shouldExecute: kemUserSession != null && !canSpeakToKem,
  });

  return {
    handleSendMessage,
    handleSendVoice,
    handleGetChatHistory,
    handleGetNextRecentHistory,
    handleResetChatHistory,
    handleCancelCurrentBotMessage,
    currentChat,
    isLoadingHistory,
    isInitialHistoryLoading,
    isLoadingMoreHistory,
    loadingBotChat,
    isTranscribing,
    kemIsTyping,
    getIsLastPage,
    hasFetchedInitialData,
    currentPage,
    onScrollToBottom,
    setLatestMessageFollowScroll,
    setChatContainerRef,
    setMessageAudioUrl,
    handleMarkMessageVisible,
    hasSentPrompt,
    resetPromptFlag: () => setHasSentPrompt(false),
    kemHealthIsHealthy,
    kemSocketReachable,
    isConnecting,
    socketState,
    isReady,
    handleRetryConnection,
    canSpeakToKem,
    kemCoachingState,
    blockSpeakToKem,
    ensureCanSpeakToKem,
    isUpgradeNoticeModalOpen,
    onCloseUpgradeNoticeModal,
    onOpenUpgradeNoticeModal,
    initiateKemUserSession,
    hasRemainingTokens,
    kemUserSession,
    isLimitReached,
  };
};

export default useKemChat;
